import { useState, useCallback } from 'react';
import { salesInsightApi } from '../api/salesInsights';
import type { SalesInsightSnapshot, GeneratePayload } from '../api/salesInsights';

export function useSalesInsights() {
  const [snapshots, setSnapshots] = useState<SalesInsightSnapshot[]>([]);
  const [activeSnapshot, setActiveSnapshot] = useState<SalesInsightSnapshot | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchSnapshots = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await salesInsightApi.list(p);
      setSnapshots(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load sales insight snapshots.');
    } finally {
      setLoading(false);
    }
  }, []);

  const generate = useCallback(async (payload: GeneratePayload = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const res = await salesInsightApi.generate(payload);
      const snapshot = res.data.data;
      setActiveSnapshot(snapshot);
      setSnapshots((prev) => [snapshot, ...prev]);
      return snapshot;
    } catch (err: any) {
      const msg = err?.message ?? 'Sales insight generation failed.';
      setError(msg);
      return null;
    } finally {
      setGenerating(false);
    }
  }, []);

  const deleteSnapshot = useCallback(async (id: number) => {
    try {
      await salesInsightApi.destroy(id);
      setSnapshots((prev) => prev.filter((s) => s.id !== id));
      if (activeSnapshot?.id === id) setActiveSnapshot(null);
    } catch {
      setError('Failed to delete snapshot.');
    }
  }, [activeSnapshot]);

  return {
    snapshots, activeSnapshot, loading, generating, error, total, page, lastPage,
    fetchSnapshots, generate, deleteSnapshot, setActiveSnapshot, setError,
  };
}
